import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import cogoToast from "cogo-toast";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaChartPie,
  FaDonate,
  FaFilter,
  FaHandHoldingWater,
  FaProjectDiagram,
  FaUsers,
  FaWater,
} from "react-icons/fa";
import { GoogleMap, Marker, useLoadScript, InfoWindow } from "@react-google-maps/api";
import { listWashProjectsPublicApi } from "../../../services/Api";
import { MAPS_API_KEY, SUCCESS_RESULT } from "../../../Constants";
import {
  applyWashFilters,
  buildFilterOptions,
  colorForKey,
  computeWashDashboardStats,
  formatCompactNumber,
  formatUsdCompact,
  WASH_SOURCE_COLORS,
} from "./washDashboardHelpers";
import "./WashDashboard.css";

const DASHBOARD_PASSCODE = "6652";

const EMPTY_FILTERS = {
  region: "",
  district: "",
  year: "",
  donor: "",
  project: "",
  water_source: "",
  work_type: "",
  water_quality: "",
  program_area: "",
};

const CHART_FILTER_LABELS = {
  region: "Region",
  district: "District",
  year: "Year",
  donor: "Donor",
  project: "Project",
  water_source: "Water source",
  work_type: "New/Rehab",
  water_quality: "Water quality",
  program_area: "Program area",
};

const chartClickName = (data) => {
  if (!data) return "";
  return String(data.name || data.payload?.name || "").trim();
};

const FilterSelect = ({ label, value, options, onChange }) => (
  <div className="wash-dash__filter">
    <label>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">All</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

const KpiCard = ({ icon: Icon, value, label }) => (
  <div className="wash-dash__kpi">
    <div className="wash-dash__kpi-icon">
      <Icon />
    </div>
    <div className="wash-dash__kpi-value">{value}</div>
    <div className="wash-dash__kpi-label">{label}</div>
  </div>
);

const Panel = ({ title, children, id, tall }) => (
  <div className={`wash-dash__panel${tall ? " wash-dash__panel--tall" : ""}`} id={id}>
    <h3 className="wash-dash__panel-title">{title}</h3>
    <div className="wash-dash__panel-body">{children}</div>
  </div>
);

const percentLabel = (value, total) => {
  if (!total) return "0%";
  return `${((value / total) * 100).toFixed(2)}%`;
};

const TABLE_COLUMNS = [
  { key: "project", label: "Project", clip: true },
  { key: "year_established", label: "Year" },
  { key: "water_source", label: "Source" },
  { key: "work_type", label: "Work" },
  { key: "region", label: "Region" },
  { key: "district", label: "District" },
  { key: "site_name", label: "Site", clip: true },
  { key: "functionality", label: "Functionality" },
  { key: "water_quality", label: "Quality", clip: true },
  { key: "donor", label: "Donor" },
  { key: "program_area", label: "Program", clip: true },
  { key: "budget_value", label: "Budget", numeric: true },
  { key: "total_beneficiaries", label: "Benef.", numeric: true },
];

const compareValues = (a, b, key, numeric) => {
  const av = a?.[key];
  const bv = b?.[key];
  if (numeric) {
    const an = Number(av);
    const bn = Number(bv);
    const aValid = av !== null && av !== undefined && av !== "" && !Number.isNaN(an);
    const bValid = bv !== null && bv !== undefined && bv !== "" && !Number.isNaN(bn);
    if (!aValid && !bValid) return 0;
    if (!aValid) return 1;
    if (!bValid) return -1;
    return an - bn;
  }
  return String(av ?? "").localeCompare(String(bv ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const WashDashboard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [activeMarker, setActiveMarker] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "project",
    direction: "asc",
  });
  const [unlocked, setUnlocked] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [passcodeError, setPasscodeError] = useState("");

  const { isLoaded: mapsLoaded } = useLoadScript({
    googleMapsApiKey: MAPS_API_KEY || "",
  });

  useEffect(() => {
    if (!unlocked) return;
    setLoading(true);
    listWashProjectsPublicApi({ status: 10001 })
      .then((res) => {
        setLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          setRows(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
          setRows([]);
          cogoToast.error(res.data?.message || "Failed to load WASH dashboard data", {
            hideAfter: 4,
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading the dashboard.",
          { hideAfter: 5 }
        );
      });
  }, [unlocked]);

  const submitPasscode = (e) => {
    if (e) e.preventDefault();
    if (String(passcode).trim() === DASHBOARD_PASSCODE) {
      setPasscodeError("");
      setUnlocked(true);
      return;
    }
    setPasscodeError("Incorrect passcode. Please try again.");
    setPasscode("");
  };
  const filterOptions = useMemo(() => buildFilterOptions(rows), [rows]);

  const districtOptions = useMemo(() => {
    if (!filters.region) return filterOptions.districts;
    return [
      ...new Set(
        rows
          .filter((r) => r.region === filters.region)
          .map((r) => r.district)
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [rows, filters.region, filterOptions.districts]);

  const filteredRows = useMemo(
    () => applyWashFilters(rows, filters),
    [rows, filters]
  );

  const sortedRows = useMemo(() => {
    const list = [...filteredRows];
    const col = TABLE_COLUMNS.find((c) => c.key === sortConfig.key);
    list.sort((a, b) => {
      const cmp = compareValues(a, b, sortConfig.key, Boolean(col?.numeric));
      return sortConfig.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [filteredRows, sortConfig]);

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const stats = useMemo(
    () => computeWashDashboardStats(filteredRows),
    [filteredRows]
  );

  const waterSourceTotal = stats.byWaterSource.reduce((s, i) => s + i.value, 0);
  const qualityTotal = stats.byWaterQuality.reduce((s, i) => s + i.value, 0);

  const mapCenter = useMemo(() => {
    if (!stats.mapPoints.length) return { lat: 5.1521, lng: 46.1996 };
    const lat =
      stats.mapPoints.reduce((s, p) => s + p.lat, 0) / stats.mapPoints.length;
    const lng =
      stats.mapPoints.reduce((s, p) => s + p.lng, 0) / stats.mapPoints.length;
    return { lat, lng };
  }, [stats.mapPoints]);

  const setFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "region") next.district = "";
      return next;
    });
  };

  /** Click a chart segment to filter the dashboard; click the same value again to clear. */
  const toggleChartFilter = (key, data) => {
    const value = typeof data === "string" ? data.trim() : chartClickName(data);
    if (!value) return;
    setFilters((prev) => {
      const next = { ...prev };
      if (prev[key] === value) {
        next[key] = "";
      } else {
        next[key] = value;
        if (key === "region") next.district = "";
      }
      return next;
    });
  };

  const cellStyle = (filterKey, name) => {
    const active = filters[filterKey];
    const isSelected = active === name;
    return {
      cursor: "pointer",
      opacity: active && !isSelected ? 0.35 : 1,
      stroke: isSelected ? "#0b2e4a" : undefined,
      strokeWidth: isSelected ? 2 : 0,
    };
  };

  const activeFilterChips = useMemo(
    () =>
      Object.entries(filters)
        .filter(([, value]) => value)
        .map(([key, value]) => ({
          key,
          value,
          label: CHART_FILTER_LABELS[key] || key,
        })),
    [filters]
  );

  if (!unlocked) {
    return (
      <div className="wash-dash wash-dash--locked">
        <Modal isOpen centered backdrop="static" keyboard={false} className="wash-dash__passcode-modal">
          <ModalHeader>Dashboard access</ModalHeader>
          <form onSubmit={submitPasscode}>
            <ModalBody>
              <p className="text-muted mb-3">
                Enter the passcode to open the Water Infrastructure Dashboard.
              </p>
              <Input
                type="password"
                inputMode="numeric"
                autoFocus
                placeholder="Passcode"
                value={passcode}
                onChange={(e) => {
                  setPasscode(e.target.value);
                  if (passcodeError) setPasscodeError("");
                }}
                invalid={Boolean(passcodeError)}
              />
              {passcodeError ? (
                <div className="text-danger small mt-2">{passcodeError}</div>
              ) : null}
            </ModalBody>
            <ModalFooter>
              <Button color="primary" type="submit">
                Unlock dashboard
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="wash-dash">
        <div className="wash-dash__loading">
          <Spinner color="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="wash-dash">
      <aside className="wash-dash__sidebar">
        <div className="wash-dash__brand">
          <img
            className="wash-dash__brand-logo"
            src="/images/white-logo.png"
            alt="Islamic Relief Somalia"
          />
          <div className="wash-dash__brand-org">ISLAMIC RELIEF SOMALIA</div>
          <h2 className="wash-dash__brand-title">
            Water Infrastructure Dashboard
          </h2>
        </div>

        <div className="wash-dash__filters-label">
          <FaFilter className="me-1" /> Global filters
        </div>
        <FilterSelect
          label="Region"
          value={filters.region}
          options={filterOptions.regions}
          onChange={(v) => setFilter("region", v)}
        />
        <FilterSelect
          label="District"
          value={filters.district}
          options={districtOptions}
          onChange={(v) => setFilter("district", v)}
        />
        <FilterSelect
          label="Year"
          value={filters.year}
          options={filterOptions.years}
          onChange={(v) => setFilter("year", v)}
        />
        <FilterSelect
          label="Donor"
          value={filters.donor}
          options={filterOptions.donors}
          onChange={(v) => setFilter("donor", v)}
        />
        <FilterSelect
          label="Project"
          value={filters.project}
          options={filterOptions.projects}
          onChange={(v) => setFilter("project", v)}
        />
        <FilterSelect
          label="Type of Water Source"
          value={filters.water_source}
          options={filterOptions.waterSources}
          onChange={(v) => setFilter("water_source", v)}
        />
        <FilterSelect
          label="New/Rehab"
          value={filters.work_type}
          options={filterOptions.workTypes}
          onChange={(v) => setFilter("work_type", v)}
        />
      </aside>

      <main className="wash-dash__main">
        <div className="wash-dash__header" id="wash-overview">
          <h1>Overview</h1>
          <p>
            Comprehensive summary of water infrastructures and impact across
            Somalia. Click any chart segment to filter the dashboard; click again
            to clear.
          </p>
          {activeFilterChips.length > 0 && (
            <div className="wash-dash__active-filters">
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className="wash-dash__filter-chip"
                  onClick={() => setFilter(chip.key, "")}
                  title="Clear this filter"
                >
                  <span>
                    {chip.label}: <strong>{chip.value}</strong>
                  </span>
                  <span aria-hidden="true">×</span>
                </button>
              ))}
              <button
                type="button"
                className="wash-dash__filter-chip wash-dash__filter-chip--clear"
                onClick={() => setFilters({ ...EMPTY_FILTERS })}
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        <div className="wash-dash__kpis" id="wash-functionality">
          <KpiCard
            icon={FaWater}
            value={stats.kpis.waterSources.toLocaleString()}
            label="# of Water sources"
          />
          <KpiCard
            icon={FaHandHoldingWater}
            value={stats.kpis.functional.toLocaleString()}
            label="Functional water source"
          />
          <KpiCard
            icon={FaUsers}
            value={formatCompactNumber(stats.kpis.beneficiaries)}
            label="# of Beneficiaries"
          />
          <KpiCard
            icon={FaProjectDiagram}
            value={stats.kpis.projects.toLocaleString()}
            label="# Of Projects"
          />
          <KpiCard
            icon={FaDonate}
            value={formatUsdCompact(stats.kpis.investment)}
            label="Total investment in USD"
          />
          <KpiCard
            icon={FaChartPie}
            value={stats.kpis.donors.toLocaleString()}
            label="# of Donors"
          />
        </div>

        <div className="wash-dash__grid">
          <Panel
            id="wash-map"
            title="Geographic distribution of water sources"
          >
            <div className="wash-dash__legend">
              {Object.entries(WASH_SOURCE_COLORS)
                .filter(([name]) =>
                  stats.byWaterSource.some((s) => s.name === name)
                )
                .map(([name, color]) => (
                  <span key={name} className="wash-dash__legend-item">
                    <span
                      className="wash-dash__legend-dot"
                      style={{ background: color }}
                    />
                    {name}
                  </span>
                ))}
            </div>
            <div className="wash-dash__map">
              {!MAPS_API_KEY ? (
                <div className="wash-dash__empty">
                  Map unavailable (missing Maps API key)
                </div>
              ) : !mapsLoaded ? (
                <div className="wash-dash__empty">
                  <Spinner size="sm" /> Loading map…
                </div>
              ) : stats.mapPoints.length === 0 ? (
                <div className="wash-dash__empty">No coordinates available</div>
              ) : (
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={mapCenter}
                  zoom={6}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: true,
                  }}
                >
                  {stats.mapPoints.map((point) => {
                    const markerProps = {
                      key: point.id,
                      position: { lat: point.lat, lng: point.lng },
                      onClick: () => setActiveMarker(point),
                    };
                    if (window.google?.maps?.SymbolPath) {
                      markerProps.icon = {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 7,
                        fillColor: colorForKey(point.water_source),
                        fillOpacity: 0.95,
                        strokeWeight: 1,
                        strokeColor: "#fff",
                      };
                    }
                    return <Marker {...markerProps} />;
                  })}
                  {activeMarker && (
                    <InfoWindow
                      position={{
                        lat: activeMarker.lat,
                        lng: activeMarker.lng,
                      }}
                      onCloseClick={() => setActiveMarker(null)}
                    >
                      <div style={{ maxWidth: 220 }}>
                        <strong>{activeMarker.site_name || "Site"}</strong>
                        <div>{activeMarker.water_source}</div>
                        <div className="text-muted small">
                          {activeMarker.district}, {activeMarker.region}
                        </div>
                        <div className="small">{activeMarker.functionality}</div>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              )}
            </div>
          </Panel>

          <Panel
            id="wash-infrastructure"
            title="Water Source Categories"
            tall
          >
            {stats.byWaterSource.length === 0 ? (
              <div className="wash-dash__empty">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                  <Pie
                    data={stats.byWaterSource}
                    dataKey="value"
                    nameKey="name"
                    cx="38%"
                    cy="50%"
                    outerRadius={130}
                    labelLine={false}
                    label={false}
                    onClick={(data) => toggleChartFilter("water_source", data)}
                    style={{ cursor: "pointer" }}
                  >
                    {stats.byWaterSource.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={colorForKey(entry.name, index)}
                        {...cellStyle("water_source", entry.name)}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      `${value} (${percentLabel(value, waterSourceTotal)})`,
                      name,
                    ]}
                  />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    wrapperStyle={{ fontSize: 12, maxWidth: "48%", lineHeight: "1.45", cursor: "pointer" }}
                    onClick={(e) => toggleChartFilter("water_source", e.value)}
                    formatter={(value) => {
                      const item = stats.byWaterSource.find((s) => s.name === value);
                      const pct = item
                        ? percentLabel(item.value, waterSourceTotal)
                        : "";
                      return `${value} (${pct})`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>

        <div className="wash-dash__grid-3">
          <Panel title="Number of Water sources Constructed or Rehabilitated each Region">
            {stats.byRegion.length === 0 ? (
              <div className="wash-dash__empty">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.byRegion}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#cfdceb" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={110}
                    tick={{ fontSize: 11 }}
                    interval={0}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    name="Water sources"
                    fill="#0b5a8c"
                    radius={[0, 4, 4, 0]}
                    onClick={(data) => toggleChartFilter("region", data)}
                    cursor="pointer"
                  >
                    {stats.byRegion.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={colorForKey(entry.name, index)}
                        {...cellStyle("region", entry.name)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <Panel id="wash-quality" title="Water Quality" tall>
            {stats.byWaterQuality.length === 0 ? (
              <div className="wash-dash__empty">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                  <Pie
                    data={stats.byWaterQuality}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="42%"
                    innerRadius={70}
                    outerRadius={115}
                    paddingAngle={1}
                    labelLine={false}
                    label={false}
                    onClick={(data) => toggleChartFilter("water_quality", data)}
                    style={{ cursor: "pointer" }}
                  >
                    {stats.byWaterQuality.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={colorForKey(entry.name, index + 3)}
                        {...cellStyle("water_quality", entry.name)}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      `${value} (${percentLabel(value, qualityTotal)})`,
                      name,
                    ]}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: 11, lineHeight: "1.4", paddingTop: 8, cursor: "pointer" }}
                    onClick={(e) => toggleChartFilter("water_quality", e.value)}
                    formatter={(value) => {
                      const item = stats.byWaterQuality.find((s) => s.name === value);
                      const pct = item
                        ? percentLabel(item.value, qualityTotal)
                        : "";
                      const short =
                        value.length > 42 ? `${value.slice(0, 40)}…` : value;
                      return `${short} (${pct})`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <Panel
            id="wash-beneficiaries"
            title="Distribution of Targeted Beneficiaries by Program Area"
          >
            {stats.beneficiariesByProgram.length === 0 ? (
              <div className="wash-dash__empty">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.beneficiariesByProgram}
                  margin={{ top: 10, right: 10, left: 0, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#cfdceb" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tickFormatter={(v) => formatCompactNumber(v)}
                    width={50}
                  />
                  <Tooltip
                    formatter={(value) => [
                      Number(value).toLocaleString(),
                      "Beneficiaries",
                    ]}
                  />
                  <Bar
                    dataKey="value"
                    fill="#1a8fbf"
                    radius={[4, 4, 0, 0]}
                    onClick={(data) => toggleChartFilter("program_area", data)}
                    cursor="pointer"
                  >
                    {stats.beneficiariesByProgram.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={colorForKey(entry.name, index)}
                        {...cellStyle("program_area", entry.name)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>

        <div className="wash-dash__grid-2">
          <Panel title="Top 4 Districts by Water Source Interventions">
            {stats.byDistrict.length === 0 ? (
              <div className="wash-dash__empty">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.byDistrict}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#cfdceb" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    tick={{ fontSize: 11 }}
                    interval={0}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    name="Interventions"
                    fill="#2bb0a6"
                    radius={[0, 4, 4, 0]}
                    onClick={(data) => toggleChartFilter("district", data)}
                    cursor="pointer"
                  >
                    {stats.byDistrict.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={colorForKey(entry.name, index)}
                        {...cellStyle("district", entry.name)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <Panel id="wash-donor" title="Water source by Donor (Top 10 Donors)">
            {stats.byDonor.length === 0 ? (
              <div className="wash-dash__empty">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.byDonor}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#cfdceb" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={110}
                    tick={{ fontSize: 10 }}
                    interval={0}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    name="Water sources"
                    radius={[0, 4, 4, 0]}
                    onClick={(data) => toggleChartFilter("donor", data)}
                    cursor="pointer"
                  >
                    {stats.byDonor.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={colorForKey(entry.name, index + 2)}
                        {...cellStyle("donor", entry.name)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>

        <div className="wash-dash__table-panel" id="wash-data-table">
          <div className="wash-dash__table-header">
            <h3>Filtered project data</h3>
            <span className="wash-dash__table-meta">
              {sortedRows.length.toLocaleString()} row(s) · click column headers to sort
            </span>
          </div>
          <div className="wash-dash__table-wrap">
            <table className="wash-dash__table">
              <thead>
                <tr>
                  {TABLE_COLUMNS.map((col) => (
                    <th key={col.key} onClick={() => toggleSort(col.key)}>
                      {col.label}
                      {sortConfig.key === col.key ? (
                        <span className="sort-ind">
                          {sortConfig.direction === "asc" ? "▲" : "▼"}
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COLUMNS.length} style={{ textAlign: "center" }}>
                      No rows for current filters
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((row) => (
                    <tr key={row.id}>
                      {TABLE_COLUMNS.map((col) => {
                        let display = row[col.key];
                        if (col.key === "budget_value") {
                          display =
                            display === null || display === undefined || display === ""
                              ? "—"
                              : Number(display).toLocaleString();
                        } else if (col.key === "total_beneficiaries") {
                          display =
                            display === null || display === undefined || display === ""
                              ? "—"
                              : Number(display).toLocaleString();
                        } else if (display === null || display === undefined || display === "") {
                          display = "—";
                        }
                        return (
                          <td
                            key={`${row.id}-${col.key}`}
                            className={col.clip ? "clip" : undefined}
                            title={col.clip ? String(row[col.key] || "") : undefined}
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WashDashboard;
